
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserRole, AgeGroup } from '@/types/user';
import { getRolePermissions } from '@/utils/rolePermissions';
import { roles } from '@/data/roleData';
import { ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import AgeGroupSelector from './AgeGroupSelector';

interface SpecialRequestFormProps {
  onBack: () => void;
}

const SpecialRequestForm = ({ onBack }: SpecialRequestFormProps) => {
  const location = useLocation();
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<AgeGroup>('13-and-under');
  const [requestedRole, setRequestedRole] = useState<UserRole>('coach');
  const [formData, setFormData] = useState({
    details: '',
    parentEmail: '',
    parentPhone: '',
    userEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Set initial values from previous form state if available
  useEffect(() => {
    if (location.state?.selectedRole) {
      setRequestedRole(location.state.selectedRole);
    }
    if (location.state?.selectedAgeGroup) {
      setSelectedAgeGroup(location.state.selectedAgeGroup);
    }
  }, [location.state]);

  const getRestrictedRoles = (ageGroup: AgeGroup): UserRole[] => {
    const allRoles: UserRole[] = ['player', 'coach', 'team-owner', 'venue', 'fan', 'parent', 'child', 'creative', 'support'];
    return allRoles.filter(role => {
      const permissions = getRolePermissions(role, ageGroup);
      return !permissions.available;
    });
  };

  const restrictedRoles = getRestrictedRoles(selectedAgeGroup);
  const roleData = roles.filter(role => restrictedRoles.includes(role.id));

  const handleAgeGroupChange = (value: string) => {
    const ageGroup = value as AgeGroup;
    setSelectedAgeGroup(ageGroup);
    // Reset requested role if it's no longer restricted for the new age group
    const newRestrictedRoles = getRestrictedRoles(ageGroup);
    if (!newRestrictedRoles.includes(requestedRole)) {
      setRequestedRole(newRestrictedRoles[0] || 'coach');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation
    if (!formData.details.trim()) {
      alert('Please provide details about why you need this role');
      setIsSubmitting(false);
      return;
    }

    if (!formData.parentEmail.trim() || !formData.parentPhone.trim()) {
      alert('Parent/Guardian contact information is required');
      setIsSubmitting(false);
      return;
    }

    if (selectedAgeGroup === '14-17' && !formData.userEmail.trim()) {
      alert('Your email address is required');
      setIsSubmitting(false);
      return;
    }

    // Simulate form submission
    try {
      // Here you would typically send the data to your backend
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitSuccess(true);
      console.log('Special request submitted:', {
        ageGroup: selectedAgeGroup,
        requestedRole,
        ...formData
      });
    } catch (error) {
      console.error('Error submitting special request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <Card className="bg-green-900/20 border-green-500/50">
        <CardHeader>
          <CardTitle className="text-green-400 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Request Submitted Successfully
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-300">
            Your special role request has been submitted for review. An administrator will contact the provided parent/guardian email within 2-3 business days.
          </p>
          <p className="text-sm text-gray-400">
            Please keep this reference for your records: SR-{Date.now().toString().slice(-6)}
          </p>
          <Button onClick={onBack} className="w-full bg-sports-green text-black hover:opacity-90">
            Back to Registration
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Button
          type="button"
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Alert className="border-yellow-500/50 bg-yellow-500/10">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-yellow-200">
          <strong>Parent/Guardian Approval Required</strong>
          <br />
          This role requires special approval due to age restrictions. A parent or guardian must approve this account creation.
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label className="text-white mb-2 block">Select Your Age Group</Label>
            <AgeGroupSelector
              selectedAgeGroup={selectedAgeGroup}
              handleAgeGroupChange={handleAgeGroupChange}
              restrictToMinors={true}
            />
          </div>

          <div>
            <Label htmlFor="requested-role" className="text-white mb-2 block">
              Role Being Requested
            </Label>
            <Select value={requestedRole} onValueChange={(value) => setRequestedRole(value as UserRole)}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {roleData.map(role => (
                  <SelectItem key={role.id} value={role.id} className="text-white hover:bg-gray-700">
                    {role.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="details" className="text-white mb-2 block">
              Details About Why You Need This Role
            </Label>
            <Textarea
              id="details"
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              placeholder="Please explain why you need access to this role and how you plan to use it..."
              className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="parent-email" className="text-white mb-2 block">
                Parent/Guardian Email
              </Label>
              <Input
                id="parent-email"
                type="email"
                value={formData.parentEmail}
                onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                placeholder="parent@example.com"
                className="bg-gray-800 border-gray-600 text-white"
                required
              />
            </div>

            <div>
              <Label htmlFor="parent-phone" className="text-white mb-2 block">
                Parent/Guardian Phone
              </Label>
              <Input
                id="parent-phone"
                type="tel"
                value={formData.parentPhone}
                onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-gray-800 border-gray-600 text-white"
                required
              />
            </div>
          </div>

          {selectedAgeGroup === '14-17' && (
            <div>
              <Label htmlFor="user-email" className="text-white mb-2 block">
                Your Email Address
              </Label>
              <Input
                id="user-email"
                type="email"
                value={formData.userEmail}
                onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                placeholder="your@example.com"
                className="bg-gray-800 border-gray-600 text-white"
                required
              />
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-red-600 hover:bg-red-700 text-white"
        >
          {isSubmitting ? 'Submitting Request...' : 'Submit Special Request'}
        </Button>
      </form>
    </div>
  );
};

export default SpecialRequestForm;
